import { useCallback, useRef, useState } from "react";
import type { IssueRow } from "#app/lib/data";

/**
 * Flat selection state for the issue board: the id set driving the card
 * checkboxes, plus an accumulated id→row map. The map exists because board
 * columns window and evict pages — a bulk action (or a multi-card drop) still
 * needs the row after its card unmounted. Same contract as the DataTable's
 * accumulated `selectedRowData`: a stable ref, mutated alongside the set.
 */
export function useBoardSelection() {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const rowDataRef = useRef(new Map<string, IssueRow>());

  const toggle = useCallback((issue: IssueRow, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        rowDataRef.current.set(issue.issueId, issue);
        next.add(issue.issueId);
      } else {
        rowDataRef.current.delete(issue.issueId);
        next.delete(issue.issueId);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    rowDataRef.current.clear();
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  return {
    selectedIds,
    selectedRowData: rowDataRef.current,
    toggle,
    clear,
  };
}
